import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Users, Church, Activity, Coins, Cross, TrendingUp, ArrowLeft, PiggyBank, Target, Award } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { APP_NAME, APP_FULL } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtIDR = (n) => `Rp ${Number(n || 0).toLocaleString("id-ID")}`;
const COLORS = ["#17365D", "#E5B52A", "#2E7D65", "#B45309", "#8B4A9E", "#0F766E"];

export default function PublicStatistik() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/public/statistics`)
      .then((r) => setStats(r.data))
      .catch(() => setStats(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">Memuat statistik…</div>;
  }
  if (!stats) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">Tidak dapat memuat statistik.</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="relative bg-gradient-to-br from-primary via-primary to-primary/80 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 papua-motif opacity-30" />
        <div className="relative max-w-6xl mx-auto px-6 py-16">
          <Link to="/public" className="text-xs text-white/70 hover:text-white flex items-center gap-1 mb-6" data-testid="link-back-public">
            <ArrowLeft className="h-3 w-3" /> Portal Publik
          </Link>
          <div className="text-xs tracking-[0.22em] uppercase text-secondary font-bold">Transparansi</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl mt-3 leading-tight">Statistik Publik<br/>{APP_NAME}</h1>
          <p className="text-sm md:text-base mt-4 max-w-2xl opacity-90">
            Data terbuka Klasis Mimika — jumlah jemaat, kegiatan, dan realisasi anggaran. Diperbarui secara langsung.
          </p>
          <div className="text-[0.65rem] text-white/60 mt-4">Diperbarui: {new Date(stats.generated_at).toLocaleString("id-ID")}</div>
        </div>
      </header>

      {/* KPI Grid */}
      <section className="max-w-6xl mx-auto px-6 py-12 -mt-8 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Anggota Jemaat", value: stats.totals.anggota, icon: Users, color: "primary" },
            { label: "Jemaat GPDP", value: stats.totals.jemaat, icon: Church, color: "secondary" },
            { label: "Kegiatan Publik", value: stats.totals.kegiatan_publik, icon: Activity, color: "primary" },
            { label: "Kegiatan Bulan Ini", value: stats.totals.kegiatan_bulan_ini, icon: TrendingUp, color: "secondary" },
          ].map((k) => {
            const Ico = k.icon;
            return (
              <Card key={k.label} className="shadow-sm" data-testid={`kpi-${k.label.toLowerCase().replace(/\s+/g,'-')}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-2">
                    <div className={`h-8 w-8 rounded-lg bg-${k.color}/10 text-${k.color} flex items-center justify-center`}>
                      <Ico className="h-4 w-4" />
                    </div>
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground font-semibold">{k.label}</div>
                  </div>
                  <div className="font-heading font-bold text-3xl mt-2">{k.value}</div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Finance summary */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <div className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-4">Keuangan Klasis</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="border-primary/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <PiggyBank className="h-5 w-5 text-primary" />
                <div className="font-heading font-bold">Realisasi Program Kerja</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Anggaran</div>
                  <div className="text-lg font-bold text-primary">{fmtIDR(stats.keuangan.program_budget)}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Realisasi</div>
                  <div className="text-lg font-bold text-secondary">{fmtIDR(stats.keuangan.program_realisasi)}</div>
                </div>
              </div>
              <Progress value={stats.keuangan.program_percent} className="h-2 mb-2" />
              <div className="text-xs text-muted-foreground">Serapan: <strong className="text-foreground">{stats.keuangan.program_percent}%</strong> dari total anggaran</div>
            </CardContent>
          </Card>
          <Card className="border-secondary/30">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <Coins className="h-5 w-5 text-secondary" />
                <div className="font-heading font-bold">Iuran Terkumpul {stats.keuangan.iuran_period && <span className="text-xs text-muted-foreground font-normal">— {stats.keuangan.iuran_period}</span>}</div>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Target</div>
                  <div className="text-lg font-bold text-primary">{fmtIDR(stats.keuangan.iuran_target)}</div>
                </div>
                <div>
                  <div className="text-[0.65rem] uppercase text-muted-foreground">Diterima</div>
                  <div className="text-lg font-bold text-emerald-700">{fmtIDR(stats.keuangan.iuran_terkumpul)}</div>
                </div>
              </div>
              <Progress value={stats.keuangan.iuran_percent} className="h-2 mb-2" />
              <div className="text-xs text-muted-foreground">Terkumpul: <strong className="text-foreground">{stats.keuangan.iuran_percent}%</strong> dari target</div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Anggota per Jemaat + Kegiatan Category */}
      <section className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Anggota per Jemaat</div>
              {stats.anggota_per_jemaat.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Belum ada data.</div>
              ) : (
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.anggota_per_jemaat} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" fontSize={10} />
                      <YAxis dataKey="name" type="category" width={80} fontSize={11} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#17365D" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="text-xs tracking-widest uppercase text-muted-foreground mb-3">Kegiatan per Kategori</div>
              {stats.kegiatan_per_kategori.length === 0 ? (
                <div className="text-sm text-muted-foreground py-8 text-center">Belum ada data.</div>
              ) : (
                <div style={{ height: 280 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={stats.kegiatan_per_kategori} dataKey="count" nameKey="category" outerRadius={90} label={({ category, percent }) => `${category} ${(percent*100).toFixed(0)}%`}>
                        {stats.kegiatan_per_kategori.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Additional KPIs */}
      <section className="max-w-6xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="p-5 flex items-center gap-3">
              <Cross className="h-6 w-6 text-primary" />
              <div>
                <div className="font-heading font-bold text-2xl">{stats.totals.gembala_jemaat}</div>
                <div className="text-xs text-muted-foreground">Gembala Jemaat aktif</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-3">
              <Award className="h-6 w-6 text-secondary" />
              <div>
                <div className="font-heading font-bold text-2xl">{stats.totals.pengurus_bpk_bp2k}</div>
                <div className="text-xs text-muted-foreground">Pengurus BPK & BP2K</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-5 flex items-center gap-3">
              <Target className="h-6 w-6 text-primary" />
              <div>
                <div className="font-heading font-bold text-2xl">{stats.totals.program}</div>
                <div className="text-xs text-muted-foreground">Program Kerja terdaftar</div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-muted/30 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-xs text-muted-foreground">
          <div>{APP_FULL}</div>
          <div className="mt-1">Data dikelola SIMPELKAMI GPDP — untuk kepentingan transparansi dan akuntabilitas jemaat.</div>
        </div>
      </footer>
    </div>
  );
}
